import { useState } from 'react';
import { useLocalBytesUrl, useOriginalUrl, useThumbnailUrl } from '@/hooks/usePhotoSources';
import { useZoomPan } from '@/hooks/useZoomPan';
import type { Extent } from '@/lib/zoomPan';
import type { PhotoRow } from '@/schema/estate';
import { readMedia } from '@/services/photoService';
import { TrashIcon, useModal } from './ui';

function viewerStatus(o: {
  checked: boolean;
  onDevice: boolean;
  downloading: boolean;
  loaded: boolean;
  failed: boolean;
}): string | undefined {
  if (!o.checked || o.onDevice) return undefined;
  if (!o.downloading || o.failed) return 'Full-size photo unavailable';
  return o.loaded ? undefined : 'Loading full size…';
}

/** One photo full screen at full resolution, stacked above whatever opened it. */
export function PhotoViewer({
  photo,
  n,
  preview,
  onClose,
  onDelete,
}: {
  photo: PhotoRow;
  n: number;
  preview: string | undefined;
  onClose: () => void;
  /** Without it, the viewer has no Delete. */
  onDelete?: () => Promise<unknown>;
}) {
  const closeRef = useModal(onClose);
  const [deleteFailed, setDeleteFailed] = useState(false);
  const remove = (del: () => Promise<unknown>) => {
    if (!window.confirm(`Delete photo ${n}?`)) return;
    setDeleteFailed(false);
    del().catch(() => setDeleteFailed(true));
  };
  const local = useLocalBytesUrl(photo.id, preview === undefined);
  const onDevice = preview ?? local.url;
  const original = readMedia(photo.storage_key)?.hash;
  const thumb = readMedia(photo.thumbnail_key)?.hash;
  const download = !onDevice && local.done ? original : undefined;
  // Originals skip useBlobUrl: its cache never revokes, and an original runs to tens of MB.
  const full = useOriginalUrl(download);
  const thumbUrl = useThumbnailUrl(download && thumb !== original ? thumb : undefined);
  const src = onDevice ?? full.url ?? thumbUrl;
  const [natural, setNatural] = useState<Extent>();
  const { stageRef, view, percent } = useZoomPan(natural);
  const status = deleteFailed
    ? 'Could not delete the photo'
    : viewerStatus({
        checked: local.done,
        onDevice: onDevice !== undefined,
        downloading: download !== undefined,
        loaded: full.url !== undefined,
        failed: full.failed,
      });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      className="fixed inset-0 z-50 flex flex-col bg-black text-white"
    >
      <div className="flex items-center justify-between gap-2 px-2 pt-[max(0.25rem,env(safe-area-inset-top))]">
        <button
          ref={closeRef}
          type="button"
          aria-label="Close photo"
          onClick={onClose}
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl text-xl hover:bg-white/10"
        >
          <span aria-hidden="true">✕</span>
        </button>
        <div className="flex items-center gap-2">
          {onDelete && (
            <button
              type="button"
              aria-label="Delete photo"
              title="Delete photo"
              onClick={() => remove(onDelete)}
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl hover:bg-white/10"
            >
              <TrashIcon />
            </button>
          )}
          <output aria-label="Zoom" className="pr-3 text-sm tabular-nums">
            {percent === undefined ? '' : `${percent}%`}
          </output>
        </div>
      </div>
      <div
        ref={stageRef}
        className="relative min-h-0 flex-1 touch-none select-none overflow-hidden"
      >
        {src && (
          <img
            src={src}
            // biome-ignore lint/a11y/noRedundantAlt: the alt matches the tile's "Photo <n>" name
            alt={`Photo ${n}`}
            draggable={false}
            onLoad={(e) =>
              setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })
            }
            className="absolute left-0 top-0 max-w-none"
            style={
              natural && view
                ? {
                    width: natural.w,
                    height: natural.h,
                    transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
                    transformOrigin: '0 0',
                  }
                : { visibility: 'hidden' }
            }
          />
        )}
        {status && (
          <output className="absolute inset-x-0 bottom-4 text-center text-sm">{status}</output>
        )}
      </div>
    </div>
  );
}
